import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import AdminHeader from '../components/AdminHeader';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { useDashboardQuery } from '../apis/dashboard';
import { getParentGroupNames } from '../apis/normalizeDashboardData';
import { hasFullFacilitatorProgramAccessRole } from '../../../utils/authRole';

const MODULE_COUNT = 6;
const SATISFACTION_LABELS = {
  1: 'Very Helpful',
  2: 'Helpful',
  3: 'A Little Helpful',
  4: 'Not Helpful',
};
const dataGridTheme = createTheme({
  palette: {
    mode: 'light',
  },
});

const formatDateTime = (date) => {
  if (!date) {
    return '';
  }

  return new Date(date).toLocaleString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

const getModuleByNumber = (modules, moduleNumber) =>
  modules.find((module) => module.moduleNumber === moduleNumber);

const formatSatisfactionRating = (rating) => {
  if (rating === null || rating === undefined || Number(rating) === 0) {
    return '';
  }

  return SATISFACTION_LABELS[rating] ?? rating;
};

const buildExportRows = (parents) =>
  parents.map((parent) => {
    const parentGroupNames = getParentGroupNames(parent);
    const row = {
      id: parent.userId,
      firstName: parent.firstName ?? '',
      lastName: parent.lastName ?? '',
      groupName: parentGroupNames.length
        ? parentGroupNames.join(', ')
        : 'Unassigned',
      surveyPreChildrenCount: parent.surveyPre?.childrenCount ?? '',
      surveyPreYoungestAge: parent.surveyPre?.youngestAge ?? '',
      surveyPreOldestAge: parent.surveyPre?.oldestAge ?? '',
      surveyPreZip: parent.surveyPre?.zip ?? '',
    };

    for (
      let moduleNumber = 1;
      moduleNumber <= MODULE_COUNT;
      moduleNumber += 1
    ) {
      const module = getModuleByNumber(parent.modules ?? [], moduleNumber);

      row[`module${moduleNumber}Completion`] = formatDateTime(
        module?.dateCompleted,
      );
      row[`module${moduleNumber}Satisfaction`] = formatSatisfactionRating(
        module?.satisfactionRating ?? module?.rating,
      );
      const practiceCompleted =
        module?.praticeCompleted ??
        module?.practiceCompleted ??
        module?.practiceCompletedAt;
      row[`module${moduleNumber}Practice`] = practiceCompleted
        ? formatDateTime(practiceCompleted)
        : '';
      row[`module${moduleNumber}Engagement`] =
        module?.engagementScore === null ||
        module?.engagementScore === undefined ||
        Number(module.engagementScore) === 0
          ? ''
          : `${module.engagementScore}%`;
    }

    return row;
  });

const columns = [
  { field: 'firstName', headerName: 'First Name', width: 160 },
  { field: 'lastName', headerName: 'Last Name', width: 160 },
  { field: 'groupName', headerName: 'Group Name', width: 200 },
  ...Array.from({ length: MODULE_COUNT }, (_, index) => {
    const moduleNumber = index + 1;

    return [
      {
        field: `module${moduleNumber}Completion`,
        headerName: `Module ${moduleNumber} - Completion`,
        width: 220,
      },
      {
        field: `module${moduleNumber}Satisfaction`,
        headerName: `Module ${moduleNumber} - Satisfaction`,
        width: 220,
      },
      {
        field: `module${moduleNumber}Practice`,
        headerName: `Module ${moduleNumber} - Practice Completion`,
        width: 240,
      },
      {
        field: `module${moduleNumber}Engagement`,
        headerName: `Module ${moduleNumber} - Engagement`,
        width: 200,
      },
    ];
  }).flat(),
  {
    field: 'surveyPreChildrenCount',
    headerName: 'Pre Survey - How many children live in your household?',
    width: 450,
  },
  {
    field: 'surveyPreYoungestAge',
    headerName: 'Pre Survey - What is the age of your youngest child?',
    width: 430,
  },
  {
    field: 'surveyPreOldestAge',
    headerName: 'Pre Survey - What is the age of your oldest child?',
    width: 410,
  },
  {
    field: 'surveyPreZip',
    headerName: 'Pre Survey - What is your ZIP code?',
    width: 310,
  },
];

const Data = () => {
  const { data: normalizedData, isLoading, isError } = useDashboardQuery();
  const dashboardLabel = hasFullFacilitatorProgramAccessRole(
    localStorage.getItem('role'),
  )
    ? 'Admin ezDashboard'
    : 'Facilitator ezDashboard';

  const rows = useMemo(
    () => buildExportRows(normalizedData?.parents ?? []),
    [normalizedData?.parents],
  );

  return (
    <div className='min-h-screen bg-[#eef6fa] text-left'>
      <AdminHeader />
      <main className='mx-auto max-w-5xl p-4'>
        <div className='my-6'>
          <h1 className='text-3xl font-bold text-dark-blue'>
            {dashboardLabel}
          </h1>
          <Link
            className='mt-2 inline-flex rounded border border-light-blue bg-white px-3 py-1.5 text-sm font-bold text-light-blue no-underline transition-colors duration-200 hover:bg-light-blue hover:text-white'
            to='/facilitator/dashboard'
          >
            Return to <i className='ml-1'>ez</i>Dashboard
          </Link>
        </div>

        <section className='my-6 rounded-lg border border-gray-200 bg-slate-50 p-4 shadow-sm'>
          <div className='mb-4 border-b border-gray-200 pb-3'>
            <h2 className='text-xl font-bold text-dark-blue'>Export Data</h2>
            <p className='mt-1 text-sm text-gray-700'>
              Showing {rows.length} parents. Use the table toolbar to filter
              columns or download a CSV.
            </p>
          </div>

          <div
            className='w-full overflow-hidden rounded border border-gray-200 bg-white'
            style={{ height: '70vh' }}
          >
            {isLoading ? (
              <div className='p-4 text-sm font-semibold text-gray-700'>
                Loading export data...
              </div>
            ) : null}
            {isError ? (
              <div className='p-4 text-sm font-semibold text-red-600'>
                Error loading export data.
              </div>
            ) : null}
            {!isLoading && !isError ? (
              <ThemeProvider theme={dataGridTheme}>
                <DataGrid
                  slots={{ toolbar: GridToolbar }}
                  rows={rows}
                  columns={columns}
                  getRowHeight={() => 'auto'}
                  disableRowSelectionOnClick
                  initialState={{
                    pagination: {
                      paginationModel: { pageSize: 50 },
                    },
                  }}
                  pageSizeOptions={[25, 50, 100]}
                />
              </ThemeProvider>
            ) : null}
          </div>
        </section>
      </main>
    </div>
  );
};

export default Data;
